// src/charts/manifest/types.ts
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
var STATE_PROPS = {
    loading: {
        type: "boolean",
        description: "In-flight fetch \u2014 shows the busy scrim."
    },
    stale: {
        type: "boolean",
        description: "Data older than the BI freshness rule."
    },
    error: {
        type: "string",
        description: "Error message to surface instead of content."
    }
};
// src/charts/components/KPICard/propsSchema.ts
var kpiCardPropsSchema = {
    type: "object",
    properties: _object_spread({
        label: {
            type: "string",
            description: "The metric label."
        },
        unit: {
            type: "string",
            description: "Unit suffix after the value (e.g. %)."
        },
        // Which value the runtime KPICard wrapper resolves off the bound envelope
        // (equipment-overview): a baseline grid metric name (baselineUtilisation, …), a
        // single-source capacity_spare scalar (`capacity` P3, `spareAtTypicalPeak` P9), or a
        // usage_bands field (`pct_over_60` P8). Omit for the plain `now`/fixture headline.
        metric: {
            type: "string",
            description: "Value source key the wrapper resolves."
        },
        // The secondary line under the value. Either a DATA DESCRIPTOR the wrapper composes
        // from the bound `capacity_spare` scalar block (single-source, equipment-overview-parity):
        //   'capacityTracked' → "{tracked} / {capacity} tracked" (P3)
        //   'absoluteMax'     → "Absolute max: {est_absolute_peak} / {capacity}" (P4)
        //   'avgSpareFree'    → "average {avg_spare} during peak hours" (P9)
        // `capacityTracked` and `avgSpareFree` DIVERGE from the 1.1 YAML templates they used to
        // copy byte-for-byte ("Capacity: {0} / {1} tracked" / "avg {0} free during peak hours") —
        // the card said "Capacity" twice, and said "Spare" in its label while saying "free" here.
        // Peter, 2026-08-07. Keep this list in step with `chartPanels.tsx`; it is the contract a
        // definition author reads.
        // — OR a LITERAL STRING rendered verbatim (EP7 — e.g. P8's "of peak hours"). The enum
        // is intentionally open (free string) so literals validate alongside descriptors; an
        // unrecognised value is treated as a literal, not an error.
        subtext: {
            type: "string",
            description: "Secondary line: a data descriptor the wrapper resolves, or a literal string."
        },
        // D4 — teal hero emphasis (semantic.interactive.accent). Additive; default false.
        highlight: {
            type: "boolean",
            description: "Render the hero value in the accent colour."
        },
        // D5 — fraction denominator descriptor. The wrapper resolves it off the bound data
        // into the KPICard `denominatorValue` prop, rendering "{value} / {denominatorValue}".
        // 'capacity' reads the single-source `baseline_view` capacity scalar (the
        // `capacitySpare.capacity` field from an `include_scalars` read), resolution-agnostic
        // by design: the descriptor stayed stable while the source moved from the interim aux
        // `usage_bands` to the single-source block when Spec A's data model landed (D5-b).
        denominator: {
            type: "string",
            enum: [
                "capacity"
            ],
            description: "Fraction denominator descriptor the wrapper resolves off the bound data."
        },
        deltaSuffix: {
            type: "string",
            description: "Suffix for the delta (e.g. pp)."
        },
        invertDelta: {
            type: "boolean",
            description: "Flip the good/bad colour mapping."
        },
        size: {
            type: "string",
            enum: [
                "sm",
                "md",
                "lg"
            ],
            description: "Emphasis size role."
        }
    }, STATE_PROPS),
    required: [
        "label"
    ],
    additionalProperties: false
};
// src/charts/components/TimeSeriesChart/propsSchema.ts
var timeSeriesChartPropsSchema = {
    type: "object",
    properties: _object_spread({
        grain: {
            type: "string",
            enum: [
                "5m",
                "hourly",
                "daily",
                "weekly",
                "monthly"
            ],
            description: "Time-axis grain (drives tick formatting)."
        },
        zone: {
            type: "string",
            description: "Twin IANA timezone for axis/label formatting."
        },
        mode: {
            type: "string",
            enum: [
                "line",
                "bar"
            ],
            description: "Line or bar rendering."
        },
        profile: {
            type: "boolean",
            description: "Hour-of-day profile mode (x = categorical hour 0\u201323; EO10)."
        },
        metricLabel: {
            type: "string",
            description: "Metric name for aria + y-axis title."
        },
        unit: {
            type: "string",
            description: "Value unit for the aria summary + y title."
        },
        /**
     * Decimal places for the hover tooltip's value (Peter, 2026-09-03: "the common component
     * should allow the definition to choose the type and precision for a given graph").
     *
     * ⭐ NOT A NEW CONVENTION — it is `MetricTable`'s authored `{ unit, precision }` column pair
     * reaching a chart. That the choice is genuinely PER PANEL is settled by the estate rather
     * than argued: `classes` authors `precision: 1` on its percentage column and `precision: 0`
     * on its counts, on one table.
     *
     * ⚠️ THIS WIDENS A SCHEMA THAT IS `additionalProperties: false`, so it moves the manifest
     * floor of any definition that AUTHORS it — see the PR body's floor table. A definition that
     * omits it keeps its current floor and takes the component's default of 1.
     */ precision: {
            type: "number",
            description: "Decimal places for the tooltip value. Default 1."
        },
        /**
     * Per-metric display identity for a METRIC-SWITCHED panel: `{ [metricKey]: { label, unit,
     * precision } }`. `ComparisonChart` has carried this shape since row 14; this is the same
     * prop on the sibling that switches through `metricMatrix`/`metricControl` the same way.
     *
     * ⭐ IT EXISTS BECAUSE PRECISION IS NOT CONSTANT WITHIN THESE PANELS. `equipment-overview`'s
     * one chart draws Utilisation (a percentage) and Headcount (people) off one control, and a
     * single scalar cannot be right for both — `43.0 people` and `12 %` are each wrong at the
     * other's setting. A missing entry degrades to the scalar `precision`, then to the default.
     */ metricMeta: {
            type: "object",
            description: "Map metricKey \u2192 { label, unit, precision } for a metric-switched panel."
        },
        // D6 — x-axis title (e.g. "Hour of Day"); rendered via chartTheme.axis.
        xAxisLabel: {
            type: "string",
            description: "x-axis title (e.g. Hour of Day)."
        },
        // D3 — definition-authored 2×2 map "{metricKind}|{aggregation}" → baseline metric
        // key, driving the profile from the two SegmentedControl instances (client-side).
        metricMatrix: {
            type: "object",
            description: 'Map "{metricKind}|{aggregation}" \u2192 baseline metric key (profile switch).'
        },
        showPeakBands: {
            type: "boolean",
            description: "Opt-in peak-hour bands (default off)."
        },
        // CP4 / item T. Declared so a definition can override the default (on for multi-series,
        // off for one); position and style are deliberately NOT authorable.
        legend: {
            type: "boolean",
            description: "Show the legend (position/style are locked). Default: on for multi-series."
        }
    }, STATE_PROPS),
    required: [
        "grain"
    ],
    additionalProperties: false
};
// src/charts/components/EntitySpotlight/propsSchema.ts
var entitySpotlightPropsSchema = {
    type: "object",
    properties: {
        bid: {
            type: "string",
            description: "BID to spotlight on activation."
        }
    },
    required: [
        "bid"
    ],
    additionalProperties: false
};
// src/charts/components/RankedRows/propsSchema.ts
var rankedRowsPropsSchema = {
    type: "object",
    properties: _object_spread({
        unit: {
            type: "string",
            description: "Value unit suffix (default %)."
        },
        maxValue: {
            type: "number",
            description: "Max for bar scaling. Omitted: 100 for a % unit, else the rows own maximum \u2014 MetricTable.scaleMax three-tier rule."
        },
        // Spelled to match `BarChart` and `MetricTable` — the house majority (two sites), and the
        // only one of the catalogue's four spellings a component `propsSchema.ts` can express.
        // ⚠️ `StatPanel`'s `{ type: 'integer', minimum: 0, maximum: 6 }` is still INEXPRESSIBLE here,
        // though for HALF the reason it used to be (corrected 2026-09-03): `JsonSchemaNode` now declares
        // `minimum` — added for `HierarchicalFilter.defaultRootIndex` — and still declares no `maximum`.
        // `StatPanel` only typechecks
        // because it is declared inline in `manifest.ts` against the looser `PanelPropsSchema`
        // (whose `properties` is `Record<string, unknown>`). Every component's own schema is typed
        // `PropsSchema`, so copying that spelling here is a type error — `BarChart`'s own comment
        // says exactly this in place. `integer` also refuses the `precision: 1.5` that
        // `ComparisonTable`'s `{ type: 'number' }` would permit.
        precision: {
            type: "integer",
            description: "Decimal places for the row value. Default 0."
        },
        inScope: {
            type: "boolean",
            description: "Whether this panel is the one currently in focus (default true) \u2014 drives the Selected ramp vs the greyscale out-of-scope ramp (brief 18 \xA72)."
        },
        emptyMessage: {
            type: "string"
        }
    }, STATE_PROPS),
    additionalProperties: false
};
// src/charts/components/KPICardLive/propsSchema.ts
var kpiCardLivePropsSchema = {
    type: "object",
    properties: _object_spread({
        label: {
            type: "string"
        },
        unit: {
            type: "string"
        },
        // MUST be declared: with `additionalProperties: false` an undeclared authored prop is
        // an unreachable feature (review: Spec Compliance/YAGNI). Brief 16 §3: the sparkline
        // needs the thicker line + frost halo when the card sits on the glass `on-scene`
        // surface, so this is which surface the DEFINITION knows it is binding onto — same
        // reasoning as PanelCard's `surface` prop.
        onGlass: {
            type: "boolean",
            description: "Set when the card sits on the glass on-scene surface, so the sparkline stays legible over the live twin."
        },
        // ops-dashboard-config-migration FR4 (ledger row 26). NAMES a key in the host's
        // `hostValues` map — it is not a value, and it is not a data binding. A live card's
        // headline comes from a transport a definition cannot express (a dGraph WebSocket
        // subscription: no endpoint, no URL, nothing for `DashboardDataProvider.fetch` to call),
        // so the definition says WHICH live reading it wants and the host supplies it. That is
        // the alternative to teaching the provider a second transport for one panel (OM-3).
        // Omit it, or bind it in a host that supplies no such key, and the card falls back to
        // the bound endpoint's headcount — correct, marginally lagged, and never blank.
        liveValue: {
            type: "string",
            description: "Name of the host-supplied live value to use as the headline (e.g. 'countEntity'). Not a data binding \u2014 the host owns the transport. Omit to use the bound endpoint's headcount."
        }
    }, STATE_PROPS),
    required: [
        "label"
    ],
    additionalProperties: false
};
// src/charts/components/EquipmentCheckTable/propsSchema.ts
var equipmentCheckTablePropsSchema = {
    type: "object",
    properties: _object_spread({
        // MUST be declared: with `additionalProperties: false` an undeclared authored prop is
        // an unreachable feature (review: Spec Compliance/Correctness/YAGNI/Frontend/Error
        // Handling/API Contract — six independent reviewers). CP7's preview board measured
        // variant B reading roughly double the characters of a long equipment name at the real
        // 420px `OpsDashboardRightPanel` width.
        spotlightVariant: {
            type: "string",
            enum: [
                "A",
                "B"
            ],
            description: "EntitySpotlight variant for equipment names. Default 'A'; 'B' costs less chrome per row for a narrow/dense host."
        },
        emptyMessage: {
            type: "string"
        }
    }, STATE_PROPS),
    additionalProperties: false
};
// src/charts/components/ComparisonTable/propsSchema.ts
var comparisonTablePropsSchema = {
    type: "object",
    properties: _object_spread({
        // `rows` is data-supplied (arrives via the binding + the wrapper's projection), so it is not a
        // required authored prop — mirrors the StatPanel/requiresData convention.
        //
        // What a DEFINITION authors instead is `measures`: which measures of the bound side become rows,
        // in order, each with its own label, unit and precision. Same relationship `MetricTable.columns`
        // has to its rows, one axis over — the component stays general and the definition names the
        // metrics, so any leg that projects a side's `values` map can bind it.
        measures: {
            type: "array",
            minItems: 1,
            items: {
                type: "object",
                properties: {
                    /** Key into the projected side's `values` map (e.g. `utilisation_pct`). */ key: {
                        type: "string"
                    },
                    /** The row label the reader sees. Says what the number IS, including its unit where the
           *  unit is a word rather than a symbol ("Capacity (positions)"). */ label: {
                        type: "string"
                    },
                    /** Appended to the formatted number — for SYMBOL units only ('%'). A word unit belongs in
           *  the label, or the cell reads "58 positions" three times down a column. */ unit: {
                        type: "string"
                    },
                    /**
           * Decimal places for this row. Default 0.
           *
           * 🚨 A PER-METRIC PRODUCT DECISION, decided from the metric's REAL range on real data and
           * never copied from the row above (Peter's standing ruling; pattern note UT §15.24). In a
           * CELL, over-rounding does not merely omit a mark as it does on a chart — it FABRICATES a
           * number, and a printed `0%` for a real 0.34 % reading sits one glyph from the `—` that
           * means "not measurable" (MCT §15.5).
           */ precision: {
                        type: "number"
                    }
                },
                required: [
                    "key",
                    "label"
                ],
                additionalProperties: false
            }
        },
        /** Column header for the label column. Screen-reader-only unless `showLabelHeader`. */ labelHeader: {
            type: "string"
        },
        /** Render `labelHeader` visibly — for a table whose rows are METRICS rather than entities. */ showLabelHeader: {
            type: "boolean"
        },
        /** Grid track override for the label column. Omit for the MEASURED 120px floor (Rob item 10). */ labelWidth: {
            type: "string"
        },
        /** Accessible name for the table. */ ariaLabel: {
            type: "string"
        },
        // FALLBACK A/B column headers. The wrapper OVERRIDES these from the served rows where it can
        // ("{group name} · {gym name}"), so the header cannot disagree with the column beneath it.
        //
        // ⚠️ THEY ARE WHAT A FAILED OR EMPTY SIDE REPORTS. A side whose read failed has no served row, so
        // its column head — and the error message naming it — comes from here. Author something a reader
        // can act on ("Gym A", never "A"). Resolving a failed side's gym NAME would need the wrapper to
        // know which control owns that rail's twin, which is two more authored props for one message;
        // recorded as a follow-up rather than built.
        aLabel: {
            type: "string"
        },
        bLabel: {
            type: "string"
        },
        /** The table's unit, used by any `measures` entry that declares none. */ unit: {
            type: "string"
        },
        /** The table's decimal places, used by any `measures` entry that declares none. */ precision: {
            type: "number"
        },
        // Authored per dashboard: this component is data-agnostic, so whether the Δ column carries
        // a good/bad verdict — and which direction is "good" — is the definition's call, not the
        // component's. Default 'none' (neutral). MUST be declared: with
        // `additionalProperties: false` an undeclared authored prop is an unreachable feature.
        //
        // ⚠️ 1.2a authors `none`, deliberately. Its rows are FIVE DIFFERENT METRICS, so one table-wide
        // direction cannot be right for all of them — and even per metric, "gym A is busier than gym B"
        // is not good or bad news. A verdict here would be the component asserting something the data
        // cannot support, which is the exact reason the default is neutral.
        deltaTone: {
            type: "string",
            enum: [
                "none",
                "up-good",
                "down-good"
            ],
            description: "Whether the \u0394 carries a verdict, and which direction is good. Default none."
        },
        // Same rationale and same MUST-be-declared rule as `deltaTone` above (review:
        // Spec Compliance/Correctness/YAGNI/Frontend/Error Handling/API Contract).
        spotlightVariant: {
            type: "string",
            enum: [
                "A",
                "B"
            ],
            description: "EntitySpotlight variant for row labels. Default 'A'; 'B' costs less chrome per row for a narrow/dense host."
        },
        emptyMessage: {
            type: "string"
        }
    }, STATE_PROPS),
    additionalProperties: false
};
// src/charts/components/MetricTable/propsSchema.ts
var metricTablePropsSchema = {
    type: "object",
    properties: _object_spread({
        columns: {
            type: "array",
            description: "The metric columns, in display order. Each names a key in the projected row values.",
            // A table with no metric columns is a list of names. `required: ['columns']` alone does not say
            // that — `columns: []` satisfies it.
            minItems: 1,
            items: {
                type: "object",
                properties: {
                    key: {
                        type: "string",
                        description: "Key into the projected row values, or into `labels` for a 'text' column."
                    },
                    header: {
                        type: "string"
                    },
                    kind: {
                        type: "string",
                        enum: [
                            "metric",
                            "text"
                        ],
                        description: "Default 'metric' (a formatted number). 'text' renders a dimension label verbatim, and the column appears only where at least one row supplies its key \u2014 which is how dimension columns follow a split level (MC-F3\u2460)."
                    },
                    // ⛔ A RULED OVERRIDE OF DESIGN CARD 11 (Peter 2026-08-17, MC-F3②), declared so a definition
                    // can reach it. The card says `--v` belongs to ONE metric per table; the peak-hours column
                    // must carry the same indicator as average utilisation, so a second column may have its own.
                    // Opt-in per column, so no existing table gains one. See docs/specs/design-ruling-table-second-indicator.md.
                    indicator: {
                        type: "boolean",
                        description: "Draw a per-cell coloured indicator from this cell's own value, independent of the row rail. Ruled override of card 11 \u2014 see docs/specs/design-ruling-table-second-indicator.md."
                    },
                    unit: {
                        type: "string",
                        description: "Appended to the number, e.g. '%'."
                    },
                    // Per-metric, decided from the metric's REAL reachable range and never copied from a
                    // sibling column (UT §15.24, Peter's standing ruling). In a TABLE the failure mode is
                    // worse than on a bar chart: a flattened precision does not remove a mark, it prints a
                    // number that is not true — `0%` for a 0.33% measurement, one glyph away from the `—`
                    // that means "not measurable".
                    precision: {
                        type: "integer",
                        description: "Decimal places for this column. Default 0."
                    },
                    scaleMax: {
                        type: "number",
                        description: "Ceiling the row rail is measured against when this column drives it. Default 100 for a % column, else the column max."
                    },
                    align: {
                        type: "string",
                        enum: [
                            "left",
                            "right"
                        ]
                    },
                    width: {
                        type: "string",
                        description: "CSS grid track override. Omit for the measured default (see MetricTable)."
                    },
                    sortable: {
                        type: "boolean",
                        description: "Default true."
                    }
                },
                required: [
                    "key",
                    "header"
                ],
                // Not `additionalProperties: false` by accident — a mistyped column key inside a nested
                // object is exactly the kind of silent authoring error this whole schema layer exists for.
                additionalProperties: false
            }
        },
        labelHeader: {
            type: "string",
            description: 'Header for the name column. Default "Name".'
        },
        // Set false where `text` columns already carry the name — otherwise the deepest dimension column
        // and the name column show the same string (measured at model split). See MetricTable.
        showLabelColumn: {
            type: "boolean",
            description: "Render the name column. Default true; set false where text columns already carry the name."
        },
        labelWidth: {
            type: "string",
            description: "CSS grid track override for the name column."
        },
        defaultSortKey: {
            type: "string",
            description: "Column key (or 'label') to sort by on first paint."
        },
        defaultSortDirection: {
            type: "string",
            enum: [
                "asc",
                "desc"
            ]
        },
        // MC20's escape hatch. The rail follows the sorted column by default; a definition that wants
        // card 11's literal "picked at design time" behaviour pins it here. Declared so that
        // overturning the assumption costs one authored prop rather than a component change.
        railColumn: {
            type: "string",
            description: "Pin the row rail to one column instead of letting it follow the sort."
        },
        emptyMessage: {
            type: "string"
        },
        ariaLabel: {
            type: "string",
            description: "Accessible name for the table."
        }
    }, STATE_PROPS),
    // `ariaLabel` is REQUIRED, not optional. `Table` renders `role="table"`, and a table with no
    // accessible name is announced as an unnamed one — which is what `ComparisonTable` does today
    // (it never forwards the prop at all). Making it required is the cheapest way to stop the next
    // definition inheriting that.
    required: [
        "columns",
        "ariaLabel"
    ],
    additionalProperties: false
};
// src/charts/components/BarChart/propsSchema.ts
var barChartPropsSchema = {
    type: "object",
    properties: _object_spread({
        unit: {
            type: "string"
        },
        metricLabel: {
            type: "string"
        },
        maxValue: {
            type: "number"
        },
        // CP4 (brief 17 §4/§5). Both MUST be declared: with `additionalProperties: false` the
        // schema is what the validator accepts, so an undeclared authored prop is an unreachable
        // feature.
        //
        // ⚠️ CORRECTION (row 19 F5, 2026-08-14). This comment used to end "Additive and optional, so
        // neither owes a manifest-version bump", and that reasoning is WRONG for the same reason set
        // out on `valueAxisTitle` below: under `additionalProperties: false` a renderer predating the
        // key REJECTS the panel rather than ignoring the key, so widening this schema does move the
        // floor for any definition that authors the new prop. Corrected here because the sentence
        // teaches future authors the wrong rule in the very file where it matters — **not** to
        // re-litigate these two props' history: they have long shipped, nothing in the estate authors
        // `orientation`/`legend` below the version that introduced them, and no floor is being
        // restated for them. (CP6's 4->5 is a separate matter — the EntitySpotlight RENAME, which is
        // breaking.)
        orientation: {
            type: "string",
            enum: [
                "vertical",
                "horizontal"
            ],
            description: "Bar direction. Default vertical; horizontal for long or many labels."
        },
        legend: {
            type: "boolean",
            description: "Show the legend (position/style are locked). Default off."
        },
        emptyMessage: {
            type: "string"
        },
        // row 19 (D9). Decimal places for the bar VALUES, applied by the runtime wrapper before
        // the component sees them, so the canvas and the accessible summary agree. Needed because
        // BI serves `utilisation_pct` as bare `numeric` with no rounding, and `ariaSummary`
        // interpolates the value straight into text — so a screen reader reads
        // "max 41.666666666666664%" without this. Embeddable's cube ROUND()ed, which is where the
        // integer-percent expectation comes from; that rounding does not exist natively.
        // Presentation only — NOT a re-derived BI rule.
        // ⚠️ CORRECTED 2026-09-03: `JsonSchemaNode` now supports `minimum` (added for
        // `HierarchicalFilter.defaultRootIndex`) but still no `maximum`, so the full RANGE remains
        // inexpressible — and adding the lower half alone to a prop definitions already author is a
        // props widening that would move this component's manifest floor. Not enforced here. The wrapper is safe for any integer — `toFixed` accepts
        // 0..100 — and a nonsensical value is a visibly wrong chart rather than a silent failure,
        // so unlike `maxSpan` this one needs no CI backstop.
        precision: {
            type: "integer",
            description: "Decimal places for bar values. Omit to render as-is."
        },
        // row 19 (F5). D6's ratified axis-title treatment, which `TimeSeriesChart` has carried since
        // the equipment-overview visual-parity work and this mark never received: the VALUE axis
        // shows `metricLabel`, plus `unit` when the unit is a symbol like "%". Peter, 2026-08-14:
        // "Busiest Equipment's measure is a percentage, but nothing on the chart says so."
        //
        // OPT-IN, for two reasons. (1) `BandHistogram` is a preset of this component bound by SHIPPED
        // dashboard 18, and default-on would restyle a live dashboard nobody asked about. (2) On a
        // HORIZONTAL chart the title takes vertical space from the category rows, so Chart.js
        // `autoSkip` can silently drop labels — a ranked card that opts in must re-measure its label
        // count (§15.21).
        //
        // 🚨 IT DOES MOVE THE MANIFEST FLOOR — 12 → 13 — AND "ADDITIVE AND OPTIONAL" IS NOT THE TEST.
        // This schema is `additionalProperties: false`, so a renderer whose manifest predates this key
        // does not ignore it: `validatePanelProps` reports `unexpected prop "valueAxisTitle"` and the
        // panel degrades to a grey card. **Observed live** during this build — a walk against a
        // renderer bundle built before the prop degraded BOTH ranked panels with
        // `data-degrade-reason="props-invalid"` and issued zero ranking requests. So a definition
        // authoring this prop MUST declare `requiresManifestVersion: 13`.
        //
        // Same mechanism as manifest 10 (the Heatmap/CapacityTreemap widening), which says so in the
        // manifest's own comments — see §15's floor pattern note (§15.18): **the floor is a property of
        // the CAPABILITY, never of whether a prop is optional.** And note WHY this is easy to get
        // wrong: the widening is invisible in CI, which validates against the LOCAL manifest and
        // therefore always knows the new key. Only a deployed older renderer sees the rejection.
        valueAxisTitle: {
            type: "boolean",
            description: "Show a value-axis title from metricLabel (+ unit for symbol units like %). Default off. On a horizontal chart this consumes plot height \u2014 re-measure the label count."
        }
    }, STATE_PROPS),
    additionalProperties: false
};
// src/charts/components/BandHistogram/propsSchema.ts
var bandHistogramPropsSchema = {
    type: "object",
    properties: _object_spread({
        emptyMessage: {
            type: "string"
        },
        // equipment-overview-parity (P10): bar value unit. `pct` (default) = %-of-open-slots;
        // `hoursPerWeek` = the 1.1 "Hours / week" histogram (reads the server hours_per_week list).
        unitMode: {
            type: "string",
            enum: [
                "pct",
                "hoursPerWeek"
            ]
        }
    }, STATE_PROPS),
    additionalProperties: false
};
// src/charts/components/ComparisonChart/propsSchema.ts
var comparisonChartPropsSchema = {
    type: "object",
    properties: _object_spread({
        grain: {
            type: "string",
            enum: [
                "5m",
                "hourly",
                "daily",
                "weekly",
                "monthly"
            ]
        },
        zone: {
            type: "string"
        },
        /**
     * Hour-of-day PROFILE mode (row 14, Dashboard 1.2a): `x` is a categorical hour 0–23 rather than a
     * timestamp, so `grain` stops driving the ticks and the axis is capped to the hours the two gyms
     * are actually open. `grain` stays REQUIRED — every non-profile consumer must state it, and a
     * conditional requirement is not expressible here.
     */ profile: {
            type: "boolean"
        },
        /** x-axis title (D6 — "value axes say what their numbers are"). E.g. "Hour of day". */ xAxisLabel: {
            type: "string"
        },
        /**
     * Which control-state id carries the active metric. Default `metric`.
     *
     * The wrapper selects one of the metrics the bound leg returns on EVERY point of its one read, so
     * the switch is a client-side re-selection and costs zero reads. Authored rather than hardcoded
     * because a dashboard may name its control anything, and a wrapper that assumed `metric` would
     * silently draw the first series for any definition that did not.
     */ metricControl: {
            type: "string"
        },
        /**
     * The 2×2 (A9/D2). `"{metricKind}|{aggregation}"` → baseline metric key, resolved by the
     * SHARED `resolveProfileMetric`. Definition data, so no dashboard-specific mapping enters
     * code. Takes precedence over `metricControl` when authored.
     */ metricMatrix: {
            type: "object"
        },
        /**
     * Per-metric display identity: `{ [metricKey]: { label, unit, precision } }`.
     *
     * D6 requires the value axis to say what its numbers are, and only the DEFINITION knows that
     * `utilisation_pct` is a percentage while `demand_hours_per_week` is a rate in hours. A missing
     * entry degrades to no axis title rather than to a wrong one.
     *
     * ⭐ `precision` JOINS ITS SIBLINGS HERE RATHER THAN BECOMING A PROP OF ITS OWN, and that is
     * the whole reason this fix moves NO floor on `equipment-comparison`: this node is an
     * unconstrained `{ type: 'object' }`, so `validatePanelProps` — which enumerates TOP-LEVEL
     * keys under `additionalProperties: false` — never sees inside it. An older renderer accepts
     * the definition and simply ignores the key it does not read.
     *
     * ⚠️ THAT IS ALSO ITS COST, STATED: a mistyped `precison` inside this blob validates clean and
     * silently falls back. `MetricTable.columns` buys the check with a nested
     * `additionalProperties: false`; declaring the members here would do the same, and WOULD move
     * the floor. Left open deliberately, and raised in the PR's decision list.
     */ metricMeta: {
            type: "object"
        },
        /**
     * FALLBACK labels for the two sides, used only when a side returned no row to name itself from.
     *
     * The wrapper normally builds `"{group name} · {gym name}"` from the SERVED row plus
     * `context.twins`, so the label cannot disagree with the data beside it. ⚠️ A side whose read FAILED
     * has no served row, so its label — and the error message naming it — falls back to these. Author
     * something a reader can act on ("Gym A", not "A").
     */ aLabel: {
            type: "string"
        },
        bLabel: {
            type: "string"
        },
        /**
     * Metric name for the y-axis title and the accessible summary. The unit is appended to the TITLE
     * only for a symbol unit ('%'); a word unit belongs in this label.
     */ metricLabel: {
            type: "string"
        },
        unit: {
            type: "string"
        },
        /** Decimal places for the numbers in the canvas's accessible summary. Default 1. */ spokenPrecision: {
            type: "number"
        }
    }, STATE_PROPS),
    required: [
        "grain"
    ],
    additionalProperties: false
};
// src/charts/components/ComparisonKPI/propsSchema.ts
var comparisonKpiPropsSchema = {
    type: "object",
    properties: _object_spread({
        /**
     * The measure rows, running down each side's column. Each carries its OWN unit and precision
     * because a percentage, a position count and a headcount do not share either — and precision
     * is decided from the metric's real range, never copied from the row above (UT §15.24).
     */ measures: {
            type: "array",
            items: {
                type: "object",
                properties: {
                    key: {
                        type: "string"
                    },
                    label: {
                        type: "string"
                    },
                    unit: {
                        type: "string"
                    },
                    precision: {
                        type: "number"
                    },
                    /**
           * Which of the panel's bindings this measure is read from. `grid` (the default, and what
           * every measure authored before this field does) folds the `baseline_view` hourly grid;
           * `summary` reads a STOCK measure straight off `baseline_summary`.
           *
           * ⚠️ AUTHORED, NOT INFERRED, for the reason `required` is. The wrapper could try the grid
           * and fall back to the summary — but then a MISSPELLED metric key silently resolves to
           * the other source's `null` and prints an em dash, which on this card means "not
           * measurable" and is a statement about the gym rather than about the definition. An
           * authored source makes a typo a missing value in ONE known place.
           */ source: {
                        type: "string",
                        enum: [
                            "grid",
                            "peak",
                            "summary"
                        ]
                    },
                    /**
           * The metric token to read, when it differs from `key`.
           *
           * ⚠️ NEEDED BECAUSE TWO ROWS LEGITIMATELY READ THE SAME METRIC. "Average utilisation" and
           * "Utilisation in peak hours" are both `baselineUtilisation` — the same measure over two
           * different WINDOWS, which is a binding-level difference (`peak_hours_only`), not a
           * metric-level one. `key` stays the row's unique identity into each side's values map;
           * `metric` says what to look up. Defaults to `key`, which is what every measure authored
           * before this field means.
           */ metric: {
                        type: "string"
                    }
                },
                required: [
                    "key",
                    "label"
                ],
                additionalProperties: false
            }
        },
        /**
     * `difference` (default) → `A − B`, signed, with arrow and tone.
     * `ratio` → `max ÷ min`, with NEITHER, because a ratio is always ≥ 1.
     */ deltaMode: {
            type: "string",
            enum: [
                "difference",
                "ratio"
            ]
        },
        /** Which measure the summary line is computed over. Defaults to the first. */ summaryMeasure: {
            type: "string"
        },
        /** The noun in "N× more {noun} for {winner}". */ summaryNoun: {
            type: "string"
        },
        /** Ratios below this read "roughly equal" rather than as a multiple (Embeddable used 1.05). */ roughlyEqualBelow: {
            type: "number"
        },
        /** Shown only when BOTH sides are absent — one absent side renders as dashes, not an empty card. */ emptyMessage: {
            type: "string"
        },
        /** Fallback side names, used only when a side's SELECTION cannot be named. */ aLabel: {
            type: "string"
        },
        bLabel: {
            type: "string"
        }
    }, STATE_PROPS),
    required: [
        "measures"
    ],
    additionalProperties: false
};
// src/charts/components/Heatmap/propsSchema.ts
var heatmapPropsSchema = {
    type: "object",
    properties: _object_spread({
        // `weekdays` RENAMED to `rows` at row 17, with no alias kept. Safe by construction: this mark
        // was `runtime: false` until the same release, so no definition could bind it and none authored
        // the old name. A compatibility alias here would be a second spelling with zero consumers.
        rows: {
            type: "array",
            items: {
                type: "string"
            },
            description: "Row order, top first. Any vocabulary; defaults to Monday\u2026Sunday."
        },
        colorMetricMatrix: {
            type: "object",
            description: "Map an aggregation control value \u2192 the metric that drives the COLOUR, when that differs from the displayed one (e.g. always a utilisation, whatever the reader selected)."
        },
        // EXACTLY three, and the schema says so rather than leaving the runtime to decide silently.
        // `asStringTriple` in `chartPanels.tsx` requires `length === 3` and returns `undefined`
        // otherwise, so before this a definition with 2 or 4 labels validated cleanly and then fell back
        // to the numeric legend with nothing said anywhere (Copilot, #1264). `minItems`/`maxItems` move
        // the failure to CI and to the console Import, where the author is.
        rampLabels: {
            type: "array",
            items: {
                type: "string"
            },
            minItems: 3,
            maxItems: 3,
            description: 'Three words describing what the MEASURE means here, replacing the numeric scale. They sit at the low, lower-middle and upper-middle of the ramp (0%, 33%, 67%) \u2014 NOT at its top: the legend supplies its own "Full" at 100% and "Over capacity" for the tail beyond it, on every consumer, and neither can be reworded or suppressed from a definition.'
        },
        rowHeaderMatrix: {
            type: "object",
            description: 'Map a level control\'s value \u2192 the row noun (e.g. {"zone":"Zone"}). Resolved client-side, the same shape as `metricMatrix`. Falls back to the static `rowHeader`.'
        },
        rowHeader: {
            type: "string",
            description: "What the rows ARE, singular and capitalised (e.g. 'Zone'). The companion table's row-header column and the noun in its caption. Default 'Weekday'."
        },
        hours: {
            type: "array",
            items: {
                type: "integer"
            }
        },
        // D3's 2×2 lens map, the SAME spelling `TimeSeriesChart` already carries: definition-authored
        // "{metricKind}|{aggregation}" → baseline metric token, resolved client-side by
        // `resolveProfileMetric`. It is what makes the metric switch cost ZERO requests — all four
        // tokens ride the one grid read (D3) — and it is why no metric name is hard-coded in a wrapper.
        metricMatrix: {
            type: "object",
            description: 'Map "{metricKind}|{aggregation}" \u2192 baseline metric key (the lens switch).'
        },
        unit: {
            type: "string"
        },
        metricLabel: {
            type: "string"
        },
        precision: {
            type: "integer",
            description: "Decimal places for the cell value, in the tooltip and the table. Default 0."
        },
        showRampLegend: {
            type: "boolean"
        },
        emptyMessage: {
            type: "string"
        }
    }, STATE_PROPS),
    additionalProperties: false
};
// src/charts/components/CapacityTreemap/propsSchema.ts
var capacityTreemapPropsSchema = {
    type: "object",
    properties: _object_spread({
        unit: {
            type: "string"
        },
        sizeLabel: {
            type: "string"
        },
        // D3's 2×2 lens map, the SAME spelling `TimeSeriesChart` already carries: definition-authored
        // "{metricKind}|{aggregation}" → baseline metric token, resolved client-side by
        // `resolveProfileMetric`. It is what makes the metric switch cost ZERO requests — all four
        // tokens ride the one grid read (D3) — and it is why no metric name is hard-coded in a wrapper.
        metricMatrix: {
            type: "object",
            description: 'Map "{metricKind}|{aggregation}" \u2192 baseline metric key (the lens switch).'
        },
        metricLabel: {
            type: "string"
        },
        // Row 17. ONE choke point on this mark (`colourText`) feeds the tile label, the tooltip AND the
        // companion table's metric column, so a single prop moves all three together.
        precision: {
            type: "integer",
            description: "Decimal places for the colour measure, on the tile and in the table. Default 0."
        },
        colorMetricMatrix: {
            type: "object",
            description: "Map an aggregation control value \u2192 the metric that drives the COLOUR, when that differs from the displayed one (e.g. always a utilisation, whatever the reader selected)."
        },
        // EXACTLY three — see the identical note on `Heatmap`'s `rampLabels`. Both marks read the same
        // `asStringTriple`, so both schemas carry the same constraint.
        rampLabels: {
            type: "array",
            items: {
                type: "string"
            },
            minItems: 3,
            maxItems: 3,
            description: 'Three words describing what the MEASURE means here, replacing the numeric scale. They sit at the low, lower-middle and upper-middle of the ramp (0%, 33%, 67%) \u2014 NOT at its top: the legend supplies its own "Full" at 100% and "Over capacity" for the tail beyond it, on every consumer, and neither can be reworded or suppressed from a definition.'
        },
        rowHeaderMatrix: {
            type: "object",
            description: 'Map a level control\'s value \u2192 the row noun (e.g. {"zone":"Zone"}). Resolved client-side, the same shape as `metricMatrix`. Falls back to the static `rowHeader`.'
        },
        rowHeader: {
            type: "string",
            description: "What the tiles ARE, singular and capitalised (e.g. 'Zone'). The companion table's row-header column and the noun in its caption. Default 'Area'."
        },
        showRampLegend: {
            type: "boolean"
        },
        emptyMessage: {
            type: "string"
        }
    }, STATE_PROPS),
    additionalProperties: false
};
// src/charts/components/Suggestions/propsSchema.ts
var suggestionsPropsSchema = {
    type: "object",
    properties: _object_spread({
        emptyMessage: {
            type: "string"
        }
    }, STATE_PROPS),
    additionalProperties: false
};
// src/charts/components/HierarchicalFilter/propsSchema.ts
var hierarchicalFilterPropsSchema = {
    type: "object",
    properties: _object_spread({
        mode: {
            type: "string",
            enum: [
                "single",
                "multi"
            ],
            description: "Selection mode."
        },
        clearable: {
            type: "boolean",
            description: "Single-mode: reclick clears to whole-group."
        },
        initialExpanded: {
            type: "string",
            enum: [
                "none",
                "roots"
            ],
            description: "Initial tree expansion (D2). Default none (collapsed to top level)."
        },
        // The POSITIONAL root seed (Peter, 2026-09-03). `0` selects the tree's FIRST root on mount,
        // `1` the second, resolved against the hierarchy at runtime — never a hardcoded bid or name,
        // because the equipment-type roots are per-organisation CATALOGUE data. An index with no root
        // behind it (a one-root org asked for `1`) seeds nothing and keeps today's whole-group scope.
        //
        // 🚨 `integer` + `minimum` ARE BOTH ENFORCED BY BOTH PLANES — ajv at CI and the renderer's own
        // `checkValue` — so this widening MOVES THE MANIFEST FLOOR (manifest v28, derived as 27 and
        // re-derived when `fix/chart-tooltip-formatting` took 27 first). It is the v13/v16/v27
        // props-widening mechanism: `additionalProperties: false` makes a v27 renderer report
        // `unexpected prop "defaultRootIndex"` and paint a grey props-invalid card where the scope
        // rail should be. Publish the definition only AFTER the renderer carrying v28 is deployed.
        defaultRootIndex: {
            type: "integer",
            minimum: 0,
            description: "Seed the selection to the Nth root (0-based), resolved against the tree."
        },
        title: {
            type: "string"
        },
        emptyMessage: {
            type: "string"
        },
        // Opt-in whole-group scope for pickers whose bound endpoints WIDEN on an omitted `bids`
        // (`timeseries`/`ranking`): `"roots"` seeds an empty selection to the tree's ROOT node bIDs
        // (derived at runtime from the hierarchy — never hardcoded) and publishes it on mount, so a
        // first paint draws the top-level groups instead of every level at once (row 19 defect ①).
        // Absent (the default) keeps the legacy contract where an empty selection is `bids: []`.
        wholeGroupScope: {
            type: "string",
            enum: [
                "roots"
            ],
            description: "Opt-in: seed an empty selection to the hierarchy roots (row 19 defect \u2460)."
        }
    }, STATE_PROPS),
    additionalProperties: false
};
// src/charts/components/controls/propsSchemas.ts
var options = {
    type: "array",
    items: {
        type: "object",
        properties: {
            value: {
                type: "string"
            },
            label: {
                type: "string"
            }
        },
        required: [
            "value",
            "label"
        ]
    }
};
var gymSelectPropsSchema = {
    type: "object",
    properties: {
        disabled: {
            type: "boolean"
        },
        /**
     * The card heading above the picker (U6, Peter 2026-08-28).
     *
     * ⚠️ AUTHORED, NOT HARD-CODED, and the reason is `equipment-comparison`: the two pickers there
     * are not interchangeable — one heads side A and one side B — so a single baked-in "GYM" could
     * not label them. Every other control on that page carries a heading; these two were the only
     * unlabelled ones, on the dashboard whose compulsory capability is cross-gym comparison.
     */ title: {
            type: "string"
        }
    },
    additionalProperties: false
};
var roomSelectPropsSchema = {
    type: "object",
    properties: {
        allLabel: {
            type: "string"
        },
        disabled: {
            type: "boolean"
        }
    },
    additionalProperties: false
};
var gymMultiSelectPropsSchema = {
    type: "object",
    properties: {
        /**
     * The upstream twin cap for this dashboard's endpoints. Author 6 for the comparison pair.
     *
     * `integer`, not `number` — the house spelling for a COUNT, as `RankedRows.precision`
     * records ("`integer` also refuses the `precision: 1.5` that ComparisonTable's
     * `{ type: 'number' }` would permit"). `number` accepts `maxSelected: 2.5`, which renders
     * "Up to 2.5 gyms can be compared at once". Fixed now because it is free now: once 7b/7e/7f
     * publish definitions authoring this prop, narrowing number -> integer becomes a validation
     * TIGHTENING on an input existing callers send, i.e. a contract break.
     *
     * A non-positive cap is not rejected here. ⚠️ CORRECTED 2026-09-03: `JsonSchemaNode` DOES now
     * carry `minimum` (added for `HierarchicalFilter.defaultRootIndex`), so this is no longer a
     * limitation of the DSL — it is a deliberate hold, because adding a bound to a prop definitions
     * already author is a PROPS WIDENING that moves this control's manifest floor. Until that is
     * paid for, the wrapper ignores an unusable cap instead.
     */ maxSelected: {
            type: "integer"
        },
        /**
     * The endpoint's BI-read budget — `MAX_COMPARISON_READS = 24` for the comparison pair.
     *
     * Authored rather than hard-coded because it is an upstream number this package does not own,
     * and because a definition that omits it is then visibly asking for the static cap only.
     */ maxReads: {
            type: "integer"
        },
        /**
     * How many BI reads the endpoint issues per twin, per equipment LEVEL — 3 for the comparison
     * pair, which its own contract states (*"it issues three reads per twin"*).
     */ readsPerTwinPerLevel: {
            type: "integer"
        },
        /**
     * The state id of the equipment-scope control whose depth drives the budget.
     *
     * ⚠️ A STATE ID, NOT A PANEL ID, because that is what `controlState` is keyed on — and
     * `equipment-comparison` is the live proof that the two differ: its scope panels are `scope-a`
     * and `scope-b` while their STATE ids are `scopeA` and `scopeB`. `manufacturer-comparison`
     * happens to use `scope` for both, and that coincidence is exactly how this would be got wrong
     * by a lane copying it.
     *
     * So: `"scope"` on `manufacturer-comparison`; `"scopeA"` / `"scopeB"` **per side** on
     * `equipment-comparison`, which carries the same read budget.
     *
     * All three are needed together: with any of them missing the wrapper falls back to the static
     * `maxSelected`, which is a real cap and never "no cap".
     */ scopeControl: {
            type: "string"
        },
        disabled: {
            type: "boolean"
        }
    },
    additionalProperties: false
};
var dayTypeSelectPropsSchema = {
    type: "object",
    properties: {
        /** The control's label — read by the wrapper as `props.title`. */ title: {
            type: "string"
        },
        /** Retained for compatibility with any already-published definition that authors it;
     *  the wrapper does not read it (the value is renderer state, not a prop). */ value: {
            type: "string",
            enum: [
                "all",
                "weekday",
                "weekend"
            ]
        },
        disabled: {
            type: "boolean"
        }
    },
    additionalProperties: false
};
var segmentedOptions = _object_spread_props(_object_spread({}, options), {
    items: _object_spread_props(_object_spread({}, options.items), {
        properties: _object_spread_props(_object_spread({}, options.items.properties), {
            value: {
                oneOf: [
                    {
                        type: "string"
                    },
                    {
                        type: "boolean"
                    }
                ]
            }
        })
    }),
    minItems: 2
});
var segmentedControlPropsSchema = {
    type: "object",
    properties: {
        label: {
            type: "string"
        },
        options: segmentedOptions,
        size: {
            type: "string",
            enum: [
                "small",
                "medium",
                "large"
            ]
        }
    },
    required: [
        "label",
        "options"
    ],
    additionalProperties: false
};
var dateRangePickerPropsSchema = {
    type: "object",
    properties: {
        value: {
            type: "object",
            properties: {
                start: {
                    type: "string"
                },
                end: {
                    type: "string"
                }
            },
            required: [
                "start",
                "end"
            ]
        },
        min: {
            type: "string"
        },
        max: {
            type: "string"
        },
        // row 19 — the three-month rule (Peter, 2026-08-05) made DECLARATIVE. Both are
        // ISO-8601 durations restricted to the `P{n}D` form: "3 months" is authored as `P90D`
        // because a calendar month is not a fixed span and the rule is a magnitude, not a
        // calendar boundary. A static definition cannot express "today minus 90 days", which is
        // why these exist at all rather than the dates being authored literally.
        //
        // NOTE this is why the rule lives HERE and not in five definitions: QS's `_MAX_SPAN`
        // caps only `5m` and `hourly`, leaving daily/weekly/monthly explicitly unbounded, and
        // `vw_fact_usage_*_current` carry no window predicate. Nothing below the dashboard
        // enforces it. Additive and optional, so every props object that validated before
        // still does.
        //
        // ⚠️ The FORMAT is not enforced here — `JsonSchemaNode` supports no `pattern`, and the
        // wrapper treats an unparseable duration as UNSET, which for `maxSpan` would silently
        // disable a safety cap. A silent failure of the three-month rule is the worst available
        // outcome, so the format is asserted instead in `@repo/dashboards`
        // `definitions.spec.ts`, over every shipped definition. That is a better home anyway:
        // it fails CI loudly for the definitions that actually exist, which a schema `pattern`
        // could not do for a value the schema type cannot express.
        defaultWindow: {
            type: "string",
            description: "Initial window as P{n}D, ending today INCLUSIVE (e.g. P90D). Format pinned in definitions.spec.ts."
        },
        maxSpan: {
            type: "string",
            description: "Hard cap on the selectable span as P{n}D (e.g. P90D), enforced on the inputs. Format pinned in definitions.spec.ts."
        }
    },
    additionalProperties: false
};
// src/charts/components/PanelCard/propsSchema.ts
var panelCardPropsSchema = {
    type: "object",
    properties: {
        surface: {
            type: "string",
            enum: [
                "card",
                "glassCard",
                "overlayCard"
            ],
            description: "Panel surface treatment (I8)."
        }
    },
    additionalProperties: false
};
// src/charts/components/ChartHeader/propsSchema.ts
var chartHeaderPropsSchema = {
    type: "object",
    properties: {
        title: {
            type: "string"
        },
        subtitle: {
            type: "string"
        },
        // When true, the runtime wrapper renders the current scope's name (from the
        // `scope` control's state) in place of `title`, falling back to `title` /
        // "Gym Total" when no node is selected (equipment-overview P1). Additive,
        // optional — static headers (P5) simply omit it.
        useScopeTitle: {
            type: "boolean"
        }
    },
    required: [
        "title"
    ],
    additionalProperties: false
};
export { kpiCardPropsSchema, timeSeriesChartPropsSchema, entitySpotlightPropsSchema, rankedRowsPropsSchema, kpiCardLivePropsSchema, equipmentCheckTablePropsSchema, comparisonTablePropsSchema, metricTablePropsSchema, barChartPropsSchema, bandHistogramPropsSchema, comparisonChartPropsSchema, comparisonKpiPropsSchema, heatmapPropsSchema, capacityTreemapPropsSchema, suggestionsPropsSchema, hierarchicalFilterPropsSchema, gymSelectPropsSchema, roomSelectPropsSchema, gymMultiSelectPropsSchema, dayTypeSelectPropsSchema, segmentedControlPropsSchema, dateRangePickerPropsSchema, panelCardPropsSchema, chartHeaderPropsSchema };
